import { BlobClient, BlockBlobClient } from '@azure/storage-blob';
import { createContainer, getDownloadSASUrl, getFileExists, getUploadSASUrl } from './api';
import Logger from './logger';
import { unemojify } from 'node-emoji';

const getBlockBlobClient = async (containerName: string, fileName: string, apiKey: string): Promise<BlockBlobClient | undefined> => {
	const sasRes = await getUploadSASUrl(apiKey, containerName, fileName);
	if (sasRes.ok) {
		const sasUrl = await sasRes.text();
		return new BlockBlobClient(sasUrl);
	} else {
		Logger.error(`Error generating SAS url: ${sasRes.status} ${sasRes.statusText}`);
		throw new Error('Unable to authenticate to get container client from Azure');
	}
};

/**
 * Create a BlockBlobClient authorised to write fileName to the specified containerName for TOKEN_LENGTH time.
 *
 * Will create the container if it does not exist already
 *
 * @param containerName name of the container to be written to
 * @param fileName name of the file to write
 * @returns BlockBlobClient ready to be written to
 */
export const generateBlockBlobClient = async (containerName: string, fileName: string, apiKey: string): Promise<BlockBlobClient> => {
	try {
		const containerRes = await createContainer(containerName, apiKey);
		if (containerRes.ok) {
			const blobClient = await getBlockBlobClient(containerName, fileName, apiKey);
			if (blobClient) {
				return blobClient;
			} else {
				//nb: the chances of this happening are basically 0
				throw new Error(`Failed to get storage blob client`);
			}
		} else {
			Logger.error(`Error creating container: ${containerRes.status} ${containerRes.statusText}`);
			throw new Error('Request to create container failed');
		}
	} catch (e) {
		throw new Error(`Failed to create container: ${String(e)}`);
	}
};

export const checkFileExists = async (containerName: string, fileName: string, apiKey: string): Promise<boolean> => {
	try {
		const res = await getFileExists(apiKey, containerName, fileName);
		if (res.ok) {
			return true;
		} else {
			return false;
		}
	} catch (e) {
		throw new Error(`Failed to check if file existed: ${String(e)}`);
	}
};

export const downloadAzureFile = async (containerName: string, fileName: string, apiKey: string): Promise<Blob> => {
	try {
		if (await checkFileExists(containerName, fileName, apiKey)) {
			const sasRes = await getDownloadSASUrl(apiKey, containerName, fileName);
			if (sasRes.ok) {
				const downloadSas = await sasRes.text();
				const blobClient = new BlobClient(downloadSas);

				const blobRes = await blobClient.download();
				if (blobRes._response.status === 200) {
					const blobBody = await blobRes.blobBody;
					if (blobBody) {
						return blobBody;
					} else {
						throw new Error('Failed to get blob body from downloaded Azure blob');
					}
				} else {
					throw new Error(`Error occurred when downloading Azure blob, status: ${blobRes._response.status}`);
				}
			} else {
				Logger.error(`Error generating SAS url: ${sasRes.status} ${sasRes.statusText}`);
				throw new Error('Unable to authenticate to get blob client from Azure');
			}
		} else {
			throw new Error(
				`Could not locate file named ${fileName} in operation ${containerName} - check both the operation and the file exist`
			);
		}
	} catch (e) {
		throw new Error(`Uncaught error when attempting to download Azure file: ${String(e)}`);
	}
};

export const uploadAzureFile = async (containerName: string, file: File, apiKey: string): Promise<void> => {
	try {
		if (await checkFileExists(containerName, file.name, apiKey)) {
			Logger.info(`File ${file.name} already existed in ${containerName}`);
		} else {
			const blockBlobClient = await generateBlockBlobClient(containerName, file.name, apiKey);
			const res = await blockBlobClient.upload(file, file.size);
			if (res.errorCode && !res.errorCode.startsWith('2')) {
				throw new Error(`Could not upload "${file.name}" to "${containerName}" - Error ${res.errorCode}`);
			}
		}
	} catch (e) {
		throw new Error(`Uncaught error when attempting to upload Azure file: ${String(e)}`);
	}
};

/**
 * Verifies if a blob name is valid for Azure Blob Storage. Reference: https://learn.microsoft.com/en-us/rest/api/storageservices/naming-and-referencing-containers--blobs--and-metadata#blob-names
 * @param blobName potential name of the blob
 * @returns empty string if no errors found, errors otherwise
 */
export const validateBlobName = (blobName: string): string => {
	const isValid: string[] = [];

	if (blobName.length < 1 || blobName.length > 1024) {
		isValid.push('Name must be between 1 and 1024 characters.');
	}
	if (blobName.endsWith('.') || blobName.endsWith('/') || blobName.endsWith('\\') || blobName.endsWith(' ')) {
		isValid.push('Name cannot end with a period, a slash, or a space ( . / \\ ).');
	}
	if (unemojify(blobName) != blobName) {
		isValid.push('Name cannot contain emojis.');
	}
	if (blobName.match(/[<>:;"|?*]/)) {
		isValid.push('Name contains invalid characters ( < > : ; " | ? * ).');
	}

	return isValid.join(' ');
};
